// Telegram 的两种登录上下文：
// 1) Mini App：Telegram.WebApp.initData，交给后端按 Bot Token 做 HMAC 验签。
// 2) 普通浏览器：Telegram Login OIDC id_token，交给后端按 Telegram JWKS 验签。
//
// 这里解析用户 ID 只为填现有 thirdparty 接口字段，不能当作可信身份；
// 后端必须从验签成功的凭证中重新读取 Telegram 用户 ID。

interface TelegramWebAppUser {
  id: number;
}

interface TelegramWebAppApi {
  initData?: string;
  initDataUnsafe?: { user?: TelegramWebAppUser };
  isVersionAtLeast?: (version: string) => boolean;
  ready?: () => void;
  expand?: () => void;
  openInvoice?: (
    url: string,
    callback?: (status: TelegramInvoiceStatus) => void,
  ) => void;
}

export type TelegramInvoiceStatus =
  | "paid"
  | "cancelled"
  | "failed"
  | "pending";

interface TelegramLoginResult {
  id_token?: string;
  error?: string;
}

interface TelegramLoginApi {
  auth(
    options: {
      client_id: number;
      scope: Array<"profile" | "phone" | "write">;
      lang?: string;
      nonce?: string;
    },
    callback: (result: TelegramLoginResult) => void,
  ): void;
}

interface TelegramGlobal {
  WebApp?: TelegramWebAppApi;
  Login?: TelegramLoginApi;
}

function telegramGlobal(): TelegramGlobal | undefined {
  return (window as typeof window & { Telegram?: TelegramGlobal }).Telegram;
}

let miniAppSessionReady: Promise<void> = Promise.resolve();

/** Share the automatic Mini App login with features that require the project token. */
export function setTelegramMiniAppSessionReady(promise: Promise<void>): void {
  miniAppSessionReady = promise;
}

export function waitForTelegramMiniAppSession(): Promise<void> {
  return miniAppSessionReady;
}

export interface TelegramMiniAppCredential {
  initData: string;
  userId: string;
}

/** 普通浏览器没有 initData，因此返回 null。 */
export function getTelegramMiniAppCredential(): TelegramMiniAppCredential | null {
  const webApp = telegramGlobal()?.WebApp;
  const initData = webApp?.initData?.trim() ?? "";
  if (!initData) return null;

  let userId = webApp?.initDataUnsafe?.user?.id?.toString() ?? "";
  if (!userId) {
    try {
      const rawUser = new URLSearchParams(initData).get("user");
      const parsed = rawUser
        ? (JSON.parse(rawUser) as { id?: number | string })
        : null;
      userId = parsed?.id?.toString() ?? "";
    } catch {
      // openid 仅为接口兼容字段；后端必须从验签后的 initData 取真实 ID。
    }
  }

  return { initData, userId };
}

export function isTelegramMiniApp(): boolean {
  return getTelegramMiniAppCredential() !== null;
}

/** 通知 Telegram 页面已可显示，并尽量展开 Mini App。 */
export function readyTelegramMiniApp(): void {
  const webApp = telegramGlobal()?.WebApp;
  webApp?.ready?.();
  webApp?.expand?.();
}

/** Open Telegram's native invoice UI. Membership is granted by the backend
 * successful_payment webhook; a paid callback is only a signal to poll it. */
export function openTelegramInvoice(
  invoiceUrl: string,
): Promise<TelegramInvoiceStatus> {
  const webApp = telegramGlobal()?.WebApp;
  const openInvoice = webApp?.openInvoice;
  const initData = webApp?.initData?.trim() ?? "";
  if (!initData || !openInvoice || !webApp?.isVersionAtLeast?.("6.1")) {
    return Promise.reject(new Error("telegram_invoice_unavailable"));
  }
  if (!/^https:\/\/t\.me\/\$[A-Za-z0-9_-]+$/.test(invoiceUrl)) {
    return Promise.reject(new Error("invalid_telegram_invoice_url"));
  }

  webApp.ready?.();
  return new Promise<TelegramInvoiceStatus>((resolve, reject) => {
    try {
      openInvoice(invoiceUrl, resolve);
    } catch (error) {
      reject(error);
    }
  });
}

let loginScriptPromise: Promise<void> | null = null;

function loadTelegramLogin(): Promise<void> {
  if (telegramGlobal()?.Login?.auth) return Promise.resolve();
  if (loginScriptPromise) return loginScriptPromise;

  loginScriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://oauth.telegram.org/js/telegram-login.js?3";
    script.async = true;
    script.onload = () =>
      telegramGlobal()?.Login?.auth
        ? resolve()
        : reject(new Error("telegram_login_unavailable"));
    script.onerror = () => {
      loginScriptPromise = null;
      reject(new Error("telegram_login_script_failed"));
    };
    document.head.appendChild(script);
  });

  return loginScriptPromise;
}

function decodeTelegramUserId(idToken: string): string {
  let payload = idToken.split(".")[1] ?? "";
  payload = payload.replace(/-/g, "+").replace(/_/g, "/");
  while (payload.length % 4) payload += "=";
  const decoded = JSON.parse(atob(payload)) as {
    id?: string | number;
    sub?: string | number;
  };
  const id = (decoded.id ?? decoded.sub)?.toString() ?? "";
  if (!id) throw new Error("telegram_id_token_missing_user");
  return id;
}

export interface TelegramOidcCredential {
  idToken: string;
  userId: string;
}

/** 打开 Telegram 官方 OIDC 弹窗。id_token 必须由后端验签后才能登录。 */
export async function openTelegramLogin(
  clientId: string,
  lang: string,
  nonce: string,
): Promise<TelegramOidcCredential> {
  const numericClientId = Number(clientId);
  if (!Number.isSafeInteger(numericClientId) || numericClientId <= 0) {
    throw new Error("missing_telegram_client_id");
  }
  if (!nonce.trim()) throw new Error("missing_telegram_login_nonce");

  await loadTelegramLogin();
  const login = telegramGlobal()?.Login;
  if (!login) throw new Error("telegram_login_unavailable");

  return new Promise<TelegramOidcCredential>((resolve, reject) => {
    login.auth(
      {
        client_id: numericClientId,
        scope: ["profile"],
        lang: lang.split("-")[0] || "en",
        nonce,
      },
      (result) => {
        if (result.error) {
          reject(new Error(result.error));
          return;
        }
        if (!result.id_token) {
          reject(new Error("telegram_login_missing_id_token"));
          return;
        }
        try {
          resolve({
            idToken: result.id_token,
            userId: decodeTelegramUserId(result.id_token),
          });
        } catch (error) {
          reject(error);
        }
      },
    );
  });
}
